'use client'
import { useMemo } from 'react';
import { Crown } from 'lucide-react';
import { MONTHS } from '../../constants/profile.constants';
import type { ContributionDay } from '../../types/profile.types';
import { getLevelClass, groupContributionsByMonth } from '../../utils/contributionUtils';
import styles from './ContributionHeatmap.module.css';

interface ContributionHeatmapProps {
	contributions: ContributionDay[];
	totalCount: number;
	percentile: number;
}

export const ContributionHeatmap = ({
	contributions,
	totalCount,
	percentile,
}: ContributionHeatmapProps) => {
	const heatmapData = useMemo(() => {
		const monthsMap = groupContributionsByMonth(contributions);

		return Object.entries(monthsMap).map(([monthIndex, days]) => {
			const weeks: ContributionDay[][] = [];
			for (let i = 0; i < 4; i++) {
				weeks.push(days.slice(i * 4, i * 4 + 4));
			}
			const mIdx = Number.parseInt(monthIndex, 10);
			return {
				id: `month-${mIdx}`,
				month: MONTHS[mIdx],
				weeks,
			};
		});
	}, [contributions]);

	return (
		<div className={styles.heatmapCard}>
			<div className={styles.header}>
				<div className={styles.headerLeft}>
					<p className={styles.label}>Número de Contribuições</p>
					<h3 className={styles.count}>{totalCount.toLocaleString('pt-BR')}</h3>
				</div>
				<div className={styles.badge}>
					<Crown size={14} />
					<span>Você está nos {percentile}% dos usuários</span>
				</div>
			</div>

			<div className={styles.heatmapContainer}>
				<div className={styles.heatmap}>
					{heatmapData.map((monthData) => (
						<div key={monthData.id} className={styles.monthColumn}>
							<div className={styles.monthLabel}>{monthData.month}</div>
							<div className={styles.gridMonth}>
								{monthData.weeks.map((week, wIndex) => (
									<div key={`${monthData.id}-w-${wIndex}`} className={styles.weekRow}>
										{week.map((day) => (
											<div
												key={day.date}
												className={`${styles.day} ${getLevelClass(day.level, styles)}`}
												title={`${day.date}: ${day.count}`}
											/>
										))}
										{Array.from({ length: 4 - week.length }).map((_, eIndex) => (
											<div
												key={`${monthData.id}-e-${eIndex}`}
												className={`${styles.day} ${styles.dayLevel0}`}
											/>
										))}
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>

			<div className={styles.footer}>
				<p className={styles.footerText}>Contribuições do ano</p>
				<div className={styles.legend}>
					<span className={styles.legendLabel}>Menos</span>
					<div className={`${styles.legendItem} ${styles.dayLevel0}`} />
					<div className={`${styles.legendItem} ${styles.dayLevel1}`} />
					<div className={`${styles.legendItem} ${styles.dayLevel2}`} />
					<div className={`${styles.legendItem} ${styles.dayLevel3}`} />
					<span className={styles.legendLabel}>Mais</span>
				</div>
			</div>
		</div>
	);
};
